/**
 * @file useShopPickupLocation.ts
 * @description Пункт самовывоза магазина для форм способа получения
 * (phase19.24 шаг 5.1).
 *
 * Отдельный хук, потому что справочник нужен двум экранам — созданию заказа и
 * блоку согласования доставки — и обоим одинаково: «грузим / упал / не
 * заполнен». Ошибка справочника форму не блокирует: заказ создать можно, а
 * письмо с адресом — нет (об этом говорит сама карточка пункта).
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError } from "../lib/api-client";
import {
  getShopPickupLocation,
  isPickupLocationReady,
  type ShopPickupLocationDto,
} from "../lib/api-shop-pickup";

export interface ShopPickupLocationState {
  location: ShopPickupLocationDto | null;
  loading: boolean;
  error: string | null;
  /** Адрес заполнен — письмо клиенту отправить можно. */
  ready: boolean;
  reload: () => void;
}

export function useShopPickupLocation(): ShopPickupLocationState {
  const [location, setLocation] = useState<ShopPickupLocationDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getShopPickupLocation()
      .then((data) => {
        if (!cancelled) setLocation(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLocation(null);
        setError(
          err instanceof ApiError ? err.message : "справочник складов недоступен",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    location,
    loading,
    error,
    ready: isPickupLocationReady(location),
    reload,
  };
}
